<template>
  <div class="animated fadeIn usage-page">

    <!-- HEADER -->
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <div class="pk-page-header__icon pk-page-header__icon--orange">
          <i class="fas fa-chart-line"></i>
        </div>
        <div>
          <h2 class="pk-page-header__title">{{ $t('kortex.usage') }}</h2>
          <p class="pk-page-header__sub" v-if="!loading && filteredRows.length > 0">{{ filteredRows.length }} {{ $t('kortex.usageRecords') }}</p>
        </div>
      </div>
      <div class="pk-page-header__right" style="display: flex; align-items: center; gap: 8px;">
        <b-form-input
          type="month"
          v-model="selectedMonth"
          class="pk-month-picker"
          style="width: 150px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff;"
        ></b-form-input>
        <select v-model="filterUserUUID" class="usage-filter-select">
          <option value="">{{ $t('kortex.allUsers') }}</option>
          <option v-for="u in userOptions" :key="u.value" :value="u.value">{{ u.text }}</option>
        </select>
        <select v-model="filterModelTier" class="usage-filter-select">
          <option value="">{{ $t('kortex.allModels') }}</option>
          <option v-for="m in modelTierOptions" :key="m" :value="m">{{ modelTierLabel(m) }}</option>
        </select>
        <select v-model="filterBillingType" class="usage-filter-select">
          <option value="">{{ $t('kortex.allTypes') }}</option>
          <option value="quota">{{ $t('kortex.inQuota') }}</option>
          <option value="billable">{{ $t('kortex.billable') }}</option>
        </select>
        <button class="pk-btn-primary" @click="exportCSV" :disabled="filteredRows.length === 0">
          <i class="fas fa-download"></i> {{ $t('kortex.exportCsv') }}
        </button>
      </div>
    </div>

    <!-- SUMMARY CARDS -->
    <div class="usage-summary" v-if="!loading">
      <div class="usage-stat-card">
        <div class="usage-stat-card__icon"><i class="fas fa-coins"></i></div>
        <div class="usage-stat-card__body">
          <div class="usage-stat-card__value">{{ formatCreditsShort(summary.totalCredits) }}</div>
          <div class="usage-stat-card__label">{{ $t('kortex.totalCredits') }}</div>
        </div>
      </div>
      <div class="usage-stat-card">
        <div class="usage-stat-card__icon usage-stat-card__icon--blue"><i class="fas fa-arrow-right"></i></div>
        <div class="usage-stat-card__body">
          <div class="usage-stat-card__value">{{ formatNumber(summary.promptTokens) }}</div>
          <div class="usage-stat-card__label">{{ $t('kortex.promptTokens') }}</div>
        </div>
      </div>
      <div class="usage-stat-card">
        <div class="usage-stat-card__icon usage-stat-card__icon--green"><i class="fas fa-arrow-left"></i></div>
        <div class="usage-stat-card__body">
          <div class="usage-stat-card__value">{{ formatNumber(summary.completionTokens) }}</div>
          <div class="usage-stat-card__label">{{ $t('kortex.completionTokens') }}</div>
        </div>
      </div>
      <div class="usage-stat-card">
        <div class="usage-stat-card__icon usage-stat-card__icon--pink"><i class="fas fa-file-alt"></i></div>
        <div class="usage-stat-card__body">
          <div class="usage-stat-card__value">{{ formatNumber(summary.ocrSeconds) }}s</div>
          <div class="usage-stat-card__label">{{ $t('kortex.ocrSeconds') }}</div>
        </div>
      </div>
    </div>

    <!-- QUOTA vs BILLABLE breakdown -->
    <div class="usage-breakdown-row" v-if="!loading && (summary.quotaCredits > 0 || summary.billableCredits > 0)">
      <div class="usage-breakdown-card usage-breakdown-card--quota">
        <div class="usage-breakdown-card__label">{{ $t('kortex.inQuota') }}</div>
        <div class="usage-breakdown-card__value">{{ formatCreditsShort(summary.quotaCredits) }}</div>
        <div class="usage-breakdown-bar">
          <div class="usage-breakdown-bar__fill usage-breakdown-bar__fill--quota" :style="{ width: quotaPercent + '%' }"></div>
        </div>
      </div>
      <div class="usage-breakdown-card usage-breakdown-card--billable">
        <div class="usage-breakdown-card__label">{{ $t('kortex.billable') }}</div>
        <div class="usage-breakdown-card__value">{{ formatCreditsShort(summary.billableCredits) }}</div>
        <div class="usage-breakdown-bar">
          <div class="usage-breakdown-bar__fill usage-breakdown-bar__fill--billable" :style="{ width: billablePercent + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="pk-card">
      <pk-data-table
        :columns="columns"
        :rows="filteredRows"
        :loading="loading"
        empty-icon="fas fa-chart-line"
        :empty-text="$t('kortex.noUsageData')"
      >
        <template #cell(user)="{ row }">
          <span class="pk-mono">{{ userNameByUUID(row.user_uuid) }}</span>
        </template>

        <template #cell(model_tier)="{ row }">
          <span class="usage-model-badge" :class="'usage-model-badge--' + row.model_tier">{{ modelTierLabel(row.model_tier) }}</span>
        </template>

        <template #cell(prompt_tokens)="{ row }">
          <span class="usage-token-val">{{ formatNumber(row.prompt_tokens) }}</span>
        </template>

        <template #cell(completion_tokens)="{ row }">
          <span class="usage-token-val">{{ formatNumber(row.completion_tokens) }}</span>
        </template>

        <template #cell(total_tokens)="{ row }">
          <strong class="usage-token-val">{{ formatNumber((row.prompt_tokens || 0) + (row.completion_tokens || 0)) }}</strong>
        </template>

        <template #cell(credits)="{ row }">
          <span class="usage-credit-val">{{ formatCredits(row.credits) }}</span>
        </template>

        <template #cell(billing_type)="{ row }">
          <span class="usage-type-badge" :class="'usage-type-badge--' + (row.billing_type || 'quota')">
            {{ row.billing_type === 'billable' ? $t('kortex.billable') : $t('kortex.inQuota') }}
          </span>
        </template>

        <template #cell(date)="{ row }">
          <span class="pk-date">{{ row.created_at }}</span>
        </template>
      </pk-data-table>
    </div>

  </div>
</template>

<script>
export default {
  name: 'KortexUsage',
  data: function () {
    var now = new Date()
    var mm = (now.getMonth() + 1).toString().padStart(2, '0')
    var defaultMonth = now.getFullYear() + '-' + mm
    return {
      loading: false,
      rows: [],
      projectUsers: [],
      loadingUsers: false,
      selectedMonth: defaultMonth,
      filterUserUUID: '',
      filterModelTier: '',
      filterBillingType: ''
    }
  },
  computed: {
    currentUserUUID: function () {
      var user = this.$store.getters.getUser
      return user ? user.uuid : null
    },
    modelTierOptions: function () {
      var tiers = {}
      this.rows.forEach(function (r) {
        if (r.model_tier) tiers[r.model_tier] = true
      })
      return Object.keys(tiers).sort()
    },
    userOptions: function () {
      var seen = {}
      var self = this
      return self.rows
        .filter(function (r) {
          if (!r.user_uuid || seen[r.user_uuid]) return false
          seen[r.user_uuid] = true
          return true
        })
        .map(function (r) {
          return { value: r.user_uuid, text: self.userNameByUUID(r.user_uuid) }
        })
    },
    filteredRows: function () {
      var self = this
      return self.rows.filter(function (r) {
        if (self.filterUserUUID && r.user_uuid !== self.filterUserUUID) return false
        if (self.filterModelTier && r.model_tier !== self.filterModelTier) return false
        if (self.filterBillingType && r.billing_type !== self.filterBillingType) return false
        return true
      })
    },
    summary: function () {
      var s = {
        totalCredits: 0,
        promptTokens: 0,
        completionTokens: 0,
        ocrSeconds: 0,
        quotaCredits: 0,
        billableCredits: 0
      }
      this.filteredRows.forEach(function (r) {
        s.totalCredits += r.credits || 0
        s.promptTokens += r.prompt_tokens || 0
        s.completionTokens += r.completion_tokens || 0
        s.ocrSeconds += r.ocr_seconds || 0
        if (r.billing_type === 'billable') {
          s.billableCredits += r.credits || 0
        } else {
          s.quotaCredits += r.credits || 0
        }
      })
      return s
    },
    quotaPercent: function () {
      var total = this.summary.quotaCredits + this.summary.billableCredits
      if (total <= 0) return 0
      return Math.round((this.summary.quotaCredits / total) * 100)
    },
    billablePercent: function () {
      var total = this.summary.quotaCredits + this.summary.billableCredits
      if (total <= 0) return 0
      return Math.round((this.summary.billableCredits / total) * 100)
    },
    columns: function () {
      return [
        { key: 'user', label: this.$t('kortex.userUUID') },
        { key: 'model_tier', label: this.$t('kortex.modelTier') },
        { key: 'prompt_tokens', label: this.$t('kortex.promptTokens'), tdClass: 'pk-td-right', thClass: 'pk-th-right' },
        { key: 'completion_tokens', label: this.$t('kortex.completionTokens'), tdClass: 'pk-td-right', thClass: 'pk-th-right' },
        { key: 'total_tokens', label: this.$t('kortex.totalTokens'), tdClass: 'pk-td-right', thClass: 'pk-th-right' },
        { key: 'credits', label: this.$t('kortex.creditsUsed'), tdClass: 'pk-td-right', thClass: 'pk-th-right' },
        { key: 'billing_type', label: this.$t('kortex.billingType') },
        { key: 'date', label: this.$t('kortex.date') }
      ]
    }
  },
  mounted: function () {
    this.load()
  },
  watch: {
    selectedMonth: function () {
      this.loadUsage()
    }
  },
  methods: {
    load: function () {
      var self = this
      self.loading = true
      Promise.all([
        self.loadProjectUsers(),
        self.loadUsage()
      ]).finally(function () {
        self.loading = false
      })
    },
    loadUsage: function () {
      var self = this
      return self.$http.get('/luma/kortex/v1/usage', {
        params: { month: self.selectedMonth, limit: 500 }
      }).then(function (resp) {
        self.rows = (resp.data && resp.data.data) || []
      }).catch(function () {
        self.rows = []
      })
    },
    loadProjectUsers: function () {
      var self = this
      self.loadingUsers = true
      return self.$project.users().then(function (resp) {
        self.projectUsers = resp.data || []
      }).catch(function () {
        self.projectUsers = []
      }).finally(function () {
        self.loadingUsers = false
      })
    },
    userNameByUUID: function (uuid) {
      if (uuid === this.currentUserUUID) return this.$t('kortex.me')
      var user = this.projectUsers.find(function (u) { return u.user_uuid === uuid })
      if (user && user.user) {
        var name = user.user.name || ''
        var email = user.user.email || ''
        return email ? name + ' (' + email + ')' : name || uuid
      }
      return uuid || '—'
    },
    modelTierLabel: function (tier) {
      if (tier === 'free') return this.$t('kortex.modelTierFree')
      if (tier === 'standard') return this.$t('kortex.modelTierStandard')
      if (tier === 'pro') return this.$t('kortex.modelTierPro')
      return tier || '—'
    },
    formatNumber: function (val) {
      if (val === null || val === undefined) return '0'
      return Number(val).toLocaleString()
    },
    formatCredits: function (val) {
      if (val === null || val === undefined) return '0'
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    },
    formatCreditsShort: function (val) {
      if (val === null || val === undefined) return '0'
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    exportCSV: function () {
      var headers = ['User', 'Model Tier', 'Prompt Tokens', 'Completion Tokens', 'Total Tokens', 'Vision Processing Seconds', 'Credits', 'Type', 'Date']
      var self = this
      var csvRows = [headers.join(',')]
      self.filteredRows.forEach(function (r) {
        var row = [
          '"' + (self.userNameByUUID(r.user_uuid) || '').replace(/"/g, '""') + '"',
          '"' + (r.model_tier || '').replace(/"/g, '""') + '"',
          r.prompt_tokens || 0,
          r.completion_tokens || 0,
          (r.prompt_tokens || 0) + (r.completion_tokens || 0),
          r.ocr_seconds || 0,
          r.credits || 0,
          r.billing_type || 'quota',
          r.created_at || ''
        ]
        csvRows.push(row.join(','))
      })
      var blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
      var url = URL.createObjectURL(blob)
      var a = document.createElement('a')
      a.href = url
      a.download = 'kortex-usage-' + self.selectedMonth + '.csv'
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.usage-page {
  padding: 8px 0;
}

/* Filters */
.usage-filter-select {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  color: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  min-width: 130px;
  appearance: auto;
}
.usage-filter-select option {
  background: #1a1a2e;
  color: #fff;
}

/* Summary cards */
.usage-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.usage-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
}
.usage-stat-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249,115,22,0.12);
  color: #f97316;
  font-size: 14px;
  flex-shrink: 0;
}
.usage-stat-card__icon--blue { background: rgba(59,130,246,0.12); color: #3b82f6; }
.usage-stat-card__icon--green { background: rgba(34,197,94,0.12); color: #22c55e; }
.usage-stat-card__icon--pink { background: rgba(236,72,153,0.12); color: #ec4899; }
.usage-stat-card__body {
  min-width: 0;
}
.usage-stat-card__value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}
.usage-stat-card__label {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-top: 2px;
}

/* Quota / Billable breakdown */
.usage-breakdown-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}
.usage-breakdown-card {
  padding: 14px 18px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
}
.usage-breakdown-card__label {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.usage-breakdown-card__value {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 4px 0 8px;
}
.usage-breakdown-bar {
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  overflow: hidden;
}
.usage-breakdown-bar__fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s;
}
.usage-breakdown-bar__fill--quota { background: #22c55e; }
.usage-breakdown-bar__fill--billable { background: #f97316; }

/* Table cell styles */
.usage-model-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: capitalize;
}
.usage-model-badge--free {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.5);
}
.usage-model-badge--standard {
  background: rgba(59,130,246,0.12);
  color: #3b82f6;
}
.usage-model-badge--pro {
  background: rgba(245,158,11,0.12);
  color: #f59e0b;
}
.usage-token-val {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.7);
}
.usage-credit-val {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: #f97316;
  font-weight: 600;
}
.usage-type-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}
.usage-type-badge--quota {
  background: rgba(34,197,94,0.15);
  color: #22c55e;
}
.usage-type-badge--billable {
  background: rgba(249,115,22,0.15);
  color: #f97316;
}

.pk-mono {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.6);
}
.pk-date {
  color: rgba(255,255,255,0.4);
  font-size: 12px;
}
.pk-td-right { text-align: right; }
.pk-th-right { text-align: right; }
</style>
